import { useEffect, useState } from "react";
import { SparkleIcon } from "./Icons";
import * as sounds from "./sounds";

/**
 * "What's new" — a short, plain-language changelog people can actually
 * skim. Deliberately not a paragraph-per-change wall of text: one line
 * each, grouped, describing what someone can now DO rather than what was
 * refactored.
 *
 * To add a release: put a new entry at the TOP of RELEASES and bump
 * LATEST_RELEASE so the "new" dot comes back for everyone. Older entries
 * don't need to be removed — anything past 7 days old just stops showing
 * on its own (see recentReleases below).
 */

const SEEN_KEY = "dbm-release-seen";

/** Bump whenever a release is added — drives the unseen dot. */
const LATEST_RELEASE = "2026-07-23";

type Release = {
  version: string;
  date: string;
  groups: { title: string; items: string[] }[];
};

const RELEASES: Release[] = [
  {
    version: "2026-07-23",
    date: "23 July 2026",
    groups: [
      {
        title: "Calls",
        items: [
          "Fixed a bug that could wrongly say \"you're already in a call\" when you weren't.",
          "A call now ends automatically for you the moment the other person hangs up.",
          "Clearer messages when a call can't connect, instead of a generic failure.",
          "Ring timeout shortened from 30 to 20 seconds.",
        ],
      },
      {
        title: "Mobile",
        items: [
          "dbm. chat now works properly on phones — redesigned navigation, message layout, and menus for smaller screens.",
          "Fixed the message toolbar (reply, react, save, delete) being unreachable on touchscreens — tap a message to bring it up.",
        ],
      },
    ],
  },
  {
    version: "2026-07-22",
    date: "22 July 2026",
    groups: [
      {
        title: "Your profile",
        items: [
          "Set your name once, right when you sign in — that's how you'll appear everywhere in chat from then on.",
        ],
      },
      {
        title: "Notifications",
        items: [
          "A bell up top now surfaces important announcements as they happen, with a badge for anything you missed.",
        ],
      },
      {
        title: "Messaging",
        items: [
          "Mention avatars are round, not boxy, wherever they show up.",
          "Bold and Italic are now truly live — click, type, and it looks bold/italic right in the box, with no stray asterisks, before or after sending.",
          "The 'remind me later' menu no longer runs off the edge of the screen.",
          "More breathing room between the last message and the composer, so \"is typing…\" never overlaps your messages.",
        ],
      },
      {
        title: "Read receipts",
        items: [
          "The 'seen by' popup now always stays fully on screen, wherever you open it from.",
          "Who's seen a message now updates live, instead of only reflecting who'd read it the moment it was sent.",
        ],
      },
      {
        title: "Groups & channels",
        items: [
          "Making someone admin, removing admin, or removing a member now shows up instantly — no more needing to refresh.",
          "Fixed a missing profile photo and tidied up the Leave / Delete buttons in the participants panel.",
        ],
      },
      {
        title: "Notes",
        items: [
          "Add private notes to any chat or group — only you can see them, perfect for keeping track of things for yourself.",
        ],
      },
      {
        title: "Calls",
        items: [
          "Minimize an ongoing call to a small floating window so you can keep chatting — expand it back any time.",
        ],
      },
      {
        title: "Sign-in",
        items: [
          "Fixed an issue that could send more sign-in code emails than necessary.",
        ],
      },
    ],
  },
  {
    version: "2026-07-21",
    date: "21 July 2026",
    groups: [
      {
        title: "Your profile",
        items: [
          "Add a profile photo from your profile panel — it now shows everywhere in chat.",
          "Light theme is the new default. Switch back to dark any time with the toggle up top.",
          "Sign out is now available in your profile.",
        ],
      },
      {
        title: "Messaging",
        items: [
          "Shift + Enter writes a new line; Enter still sends.",
          "Type @ to mention someone and pick them from the list.",
          "Attach a file and add a caption, sent together as one message.",
          "Paste a screenshot straight into the message box.",
          "Every chat now has its own Files list in the details panel.",
          "Save a message for later — tap the bookmark, tap again to unsave.",
          "Jump straight to a saved message from your saved list.",
        ],
      },
      {
        title: "Read receipts",
        items: [
          "The eye on your messages shows when they've been seen.",
          "In groups, tap it to see exactly who has — and who hasn't — read it.",
        ],
      },
      {
        title: "Calls",
        items: [
          "Your mic and camera are checked before dialling, so problems surface early.",
          "Calls ring for 30 seconds, then hang up on their own.",
          "Answering on one tab now stops the ringing everywhere else.",
          "Redesigned the incoming and outgoing call screens.",
        ],
      },
      {
        title: "Groups & channels",
        items: [
          "A dedicated participants panel — add people, make someone an admin, or leave.",
        ],
      },
      {
        title: "Sound & search",
        items: [
          "Sounds for sending, receiving, calls and clicks.",
          "Search now finds anyone in the directory, not just people you've messaged.",
        ],
      },
    ],
  },
];

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** Releases from the last 7 days, newest first — older entries age out on
 * their own as new ones get added, rather than needing to be manually
 * deleted from RELEASES (which would also erase them from anyone who
 * hasn't opened the popup since they were pushed). */
function recentReleases(): Release[] {
  const cutoff = Date.now() - WEEK_MS;
  const recent = RELEASES.filter((r) => new Date(r.version).getTime() >= cutoff);
  return recent.length > 0 ? recent : [RELEASES[0]];
}

/** True when there's a release the user hasn't opened yet. */
export function useHasUnseenRelease(): [boolean, () => void] {
  const [unseen, setUnseen] = useState(false);

  useEffect(() => {
    try {
      setUnseen(localStorage.getItem(SEEN_KEY) !== LATEST_RELEASE);
    } catch {
      setUnseen(false); // storage blocked — don't nag on every load
    }
  }, []);

  const markSeen = () => {
    try {
      localStorage.setItem(SEEN_KEY, LATEST_RELEASE);
    } catch {
      /* ignore */
    }
    setUnseen(false);
  };

  return [unseen, markSeen];
}

export default function ReleaseNotes({ onClose }: { onClose: () => void }) {
  const releases = recentReleases();
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="release-card" onClick={(e) => e.stopPropagation()}>
        <div className="release-hero">
          <span className="release-hero__icon">
            <SparkleIcon size={20} />
          </span>
          <div>
            <div className="release-hero__title">What's new</div>
            <div className="release-hero__date">Last 7 days</div>
          </div>
          <button
            className="hbtn release-hero__close"
            onClick={() => {
              sounds.playOnce(sounds.SOUND.click);
              onClose();
            }}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="release-body">
          {releases.map((release) => (
            <div className="release-section" key={release.version}>
              <div className="release-section__date">{release.date}</div>
              {release.groups.map((group) => (
                <div className="release-group" key={group.title}>
                  <div className="release-group__title">{group.title}</div>
                  <ul className="release-list">
                    {group.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="release-foot">
          <button
            className="modal-btn"
            onClick={() => {
              sounds.playOnce(sounds.SOUND.click);
              onClose();
            }}
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
